import { CalendarOutlined } from '@ant-design/icons'
import { Modal, Space, Radio } from 'antd'
import type React from 'react'
import { useEffect, useState } from 'react'
import CreateNewEventForm from '@components/StoreMonitoring/ForecastManagement/components/EventModal/CreateNewEvent'
import AssociateExistingEvent from '@components/StoreMonitoring/ForecastManagement/components/EventModal/AssociateExistingEvent'
import { useEvents } from '@api/useEvents'
import { GraviButton } from '@gravitate-js/excalibrr'

interface AssociateEventModalProps {
  visible: boolean
  onSave: (eventId?: string, eventName?: string, eventType?: string) => void
  onSkip: () => void
  onCancel: () => void
}

const AssociateEventModal: React.FC<AssociateEventModalProps> = ({ visible, onSave, onSkip, onCancel }) => {
  const [activeTab, setActiveTab] = useState<'existing' | 'createNew'>('existing')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [eventName, setEventName] = useState<string>('')
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)

  const { metadataQuery } = useEvents()
  const { data: metadata } = metadataQuery

  const handleOk = () => {
    if (selectedEventId) {
      onSave(selectedEventId)
    } else {
      onSave(null, eventName, selectedType)
    }
  }

  const resetModal = () => {
    setActiveTab('existing')
    setSelectedType(null)
    setEventName('')
    setSelectedEventId(null)
  }

  useEffect(() => {
    if (visible) setActiveTab(metadata.events?.length === 0 ? 'createNew' : 'existing')
    else {
      resetModal()
    }
  }, [metadata, visible])

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
          Associate An Event?
        </div>
      }
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      footer={[
        <GraviButton key='skip' onClick={onSkip} buttonText='Skip' theme4 className='square-ish mr-4' />,
        <GraviButton
          key='save'
          type='primary'
          onClick={handleOk}
          buttonText='Save'
          disabled={
            (!selectedEventId && !eventName && activeTab === 'createNew') ||
            (!selectedEventId && activeTab === 'existing')
          }
          theme2
          className='square-ish mr-4'
        />,
      ]}
    >
      <Space style={{ marginBottom: 16 }}>
        <Radio.Group
          optionType='button'
          buttonStyle='solid'
          value={activeTab === 'createNew' ? 'create_new' : 'existing'}
          onChange={(e) => {
            setActiveTab(e.target.value === 'create_new' ? 'createNew' : 'existing')
            setSelectedEventId(null)
          }}
          style={{ userSelect: 'none' }}
        >
          <Radio.Button value='create_new'>Create New Event</Radio.Button>
          <Radio.Button value='existing' disabled={metadata?.events.length === 0}>
            Existing Event
          </Radio.Button>
        </Radio.Group>
      </Space>
      {activeTab === 'existing' ? (
        <AssociateExistingEvent selectedEventId={selectedEventId} setEventId={setSelectedEventId} />
      ) : (
        <CreateNewEventForm
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          name={eventName}
          setName={setEventName}
        />
      )}
    </Modal>
  )
}

export default AssociateEventModal
